import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import { formatSerpText } from '@/utils/approval/serpDocument';
import {
  buildSerpSeasonEndModalTitle,
  getSerpSeasonEndSeasonRows,
  groupSerpSeasonEndShops,
  type SerpSeasonEndDetail,
  type SerpSeasonEndSource,
} from '@/utils/approval/serpSeasonEnd';

/** 레거시 문서와 같은 id — 조회·작성 화면이 이 id 로 버튼은 포탈 버튼, 모달 틀은 포탈 모달로 바꿔 그림 */
export const SETTLEMENT_BUTTON_ID = 'settlementList';
export const SETTLEMENT_MODAL_ID = 'settlementModal';
export const SETTLEMENT_TITLE_ID = 'settlementModalLabel';
export const SETTLEMENT_CONTENT_ID = 'settlementContent';

type TablesProps = {
  source: SerpSeasonEndSource;
  detail: SerpSeasonEndDetail | null;
  t: TFunction;
};

/** 레거시 setDataDetail — 매장별 표, 빈 값(null)은 빈칸 */
const SerpSeasonEndShopTables = ({
  detail,
  t,
}: Pick<TablesProps, 'detail' | 't'>) => (
  <>
    {groupSerpSeasonEndShops(detail).map((group, index) => (
      <div key={`shop-${index}`}>
        <label className="label font-weight-bold">
          {t('매장명 : {{shop}}', {
            shop: group.shopName,
            interpolation: { escapeValue: false },
          })}
        </label>
        <table className="table table-bordered-horizontal mb-30 text-center">
          <thead>
            <tr>
              <th>{t('매장코드')}</th>
              <th>{t('매장명')}</th>
              <th>{t('공급가율')}</th>
              <th>{t('시즌')}</th>
              <th>{t('전산 재고')}</th>
              <th>{t('실재고 금액')}</th>
              <th>{t('미처리 금액')}</th>
              <th>{t('차이금액')}</th>
              <th>{t('Loss 금액')}</th>
              <th>{t('정산')}</th>
              <th>{t('비고')}</th>
            </tr>
          </thead>
          <tbody>
            {group.rows.map((row, rowIndex) => (
              <tr key={`row-${rowIndex}`}>
                <td>{formatSerpText(row.SHOP_CD)}</td>
                <td>{formatSerpText(row.SHOP_ABBR_NM)}</td>
                <td>{formatSerpText(row.GI_RAT)}</td>
                <td>{formatSerpText(row.SSN_NM)}</td>
                <td>{formatSerpText(row.STK_TOT_AMT)}</td>
                <td>{formatSerpText(row.STCKT_TOT_AMT)}</td>
                <td>{formatSerpText(row.ADJ_TOT_AMT)}</td>
                <td>{formatSerpText(row.DIFF_TOT_AMT)}</td>
                <td>{formatSerpText(row.LOSS_TOT_AMT)}</td>
                <td>{formatSerpText(row.STMT_TOT_AMT)}</td>
                <td>{formatSerpText(row.APRV_RMK)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ))}
  </>
);

/** 레거시 #APV_SERP_SEASON_END .form-group — 대상매장 칸은 레거시도 늘 빈칸 */
const SerpSeasonEndTables = ({ source, detail, t }: TablesProps) => (
  <>
    <label className="label font-weight-bold">{t('시즌 정산')}</label>
    <table className="table table-bordered-horizontal mb-30 text-center">
      <tbody>
        <tr>
          <th className="bg-light">{t('브랜드')}</th>
          <th className="bg-light">{t('대상시즌')}</th>
          <th className="bg-light">{t('정산기준일')}</th>
        </tr>
        <tr>
          <td>{formatSerpText(source.BRAND_EN_NM)}</td>
          <td>{formatSerpText(source.DL_SSN_CD)}</td>
          <td>{formatSerpText(source.INSTR_DE)}</td>
        </tr>
        <tr>
          <th colSpan={2} className="bg-light">
            {t('대상매장')}
          </th>
          <th className="bg-light">{t('정산내역보기')}</th>
        </tr>
        <tr>
          <td colSpan={2} />
          <td>
            <button type="button" id={SETTLEMENT_BUTTON_ID}>
              {t('정산내역보기')}
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <label className="label font-weight-bold">{t('시즌별 내역')}</label>
    <table className="table table-bordered-horizontal mb-30 text-center">
      <colgroup>
        <col width="60px" />
        <col width="24%" />
        <col width="24%" />
        <col width="24%" />
        <col width="auto" />
      </colgroup>
      <thead>
        <tr>
          <th>{t('구분')}</th>
          <th>{t('전산 재고 금액')}</th>
          <th>{t('실재고 + 미처리 금액')}</th>
          <th>{t('차이 금액')}</th>
          <th>{t('비고')}</th>
        </tr>
      </thead>
      <tbody>
        {getSerpSeasonEndSeasonRows(source).map((row, index) => (
          <tr key={`season-${index}`}>
            <td>{formatSerpText(row.SSN_NM)}</td>
            <td>{formatSerpText(row.STK_TOT_AMT)}</td>
            <td>{formatSerpText(row.STCKT_TOT_AMT)}</td>
            <td>{formatSerpText(row.DIFF_TOT_AMT)}</td>
            <td />
          </tr>
        ))}
      </tbody>
    </table>
    <div id={SETTLEMENT_MODAL_ID} style={{ display: 'none' }}>
      <h4 id={SETTLEMENT_TITLE_ID}>
        {buildSerpSeasonEndModalTitle(source, t)}
      </h4>
      <div id={SETTLEMENT_CONTENT_ID}>
        <SerpSeasonEndShopTables detail={detail} t={t} />
      </div>
    </div>
  </>
);

/** attributes.content — 레거시 조회 화면처럼 정산내역 모달 내용까지 저장 HTML 에 담음 */
export const buildSerpSeasonEndContentHtml = (
  source: SerpSeasonEndSource,
  detail: SerpSeasonEndDetail | null,
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <div className="form-group">
      <SerpSeasonEndTables source={source} detail={detail} t={t} />
    </div>,
  );
